import * as React from "react";

import { getProfile } from "@/api/client";
import type { ProfileView } from "@/api/types";

/**
 * Lifecycle of the current user's profile, fetched once on first
 * authenticated SPA load. Mounted directly under AuthGate so it has a
 * fresh access token available via the API client.
 *
 * <p>Why a context rather than inlining the fetch in AuthGate:</p>
 * <ul>
 *   <li>Both the route gate ({@link RequireHandle}) and the setup page
 *       ({@code ProfileSetupPage}) need to read + mutate the same profile
 *       object. The setup page calls {@code refresh()} after a successful
 *       POST so the gate sees the new handle and admits the user.</li>
 *   <li>Keeps AuthGate dumb — it stays responsible only for the OIDC
 *       sign-in dance, not for any backend coupling.</li>
 * </ul>
 */
type ProfileState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "ready"; profile: ProfileView };

interface ProfileContextValue {
  state: ProfileState;
  /** Re-fetch the profile and update the context (used after POST /profile). */
  refresh: () => Promise<void>;
  /**
   * Optimistic in-context update — used by the setup page so it doesn't
   * have to wait for a second GET round trip before navigating away.
   */
  setProfile: (profile: ProfileView) => void;
}

const ProfileContext = React.createContext<ProfileContextValue | null>(null);

export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<ProfileState>({ status: "loading" });

  const fetchOnce = React.useCallback(async () => {
    try {
      const profile = await getProfile();
      setState({ status: "ready", profile });
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setState({ status: "error", error: message });
    }
  }, []);

  React.useEffect(() => {
    void fetchOnce();
  }, [fetchOnce]);

  const value = React.useMemo<ProfileContextValue>(
    () => ({
      state,
      refresh: fetchOnce,
      setProfile: (profile) => setState({ status: "ready", profile }),
    }),
    [state, fetchOnce],
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

/** Read the current ProfileContext. Throws if used outside the provider. */
export function useProfile(): ProfileContextValue {
  const ctx = React.useContext(ProfileContext);
  if (!ctx) {
    throw new Error("useProfile must be used inside <ProfileProvider>");
  }
  return ctx;
}

/**
 * Like {@link useProfile} but returns null instead of throwing when used
 * outside the provider — for components (e.g. SignedInHeader) that also render
 * on the public agent-card page, which lives outside ProfileProvider.
 */
export function useProfileOptional(): ProfileContextValue | null {
  return React.useContext(ProfileContext);
}
